// docs/subscriptions.md tier IDs:
//   free    = Visible    (marketplace only, no AI)        AED 0
//   starter = Starter    (free + lifted limits, browse)   AED 99/mo
//   menu    = Menu       (AI menu intelligence)           AED 449/mo
//   operate = Operate    (full operations, POS)           AED 849/mo
//   scale   = Scale      (marketing, bookings, waitlist)  AED 1199/mo
//   command = Command    (enterprise, multi-location)     AED 1899/mo
//   hotel   = Hotel      (Scale + in-room dining)         AED 1199/mo
//
// `hotel` sits on the SAME rung as `scale` (see PLAN_ORDER), so it inherits everything
// Scale has. In-room dining is NOT a rung above it — see ROOM_SERVICE_PLANS in plans.ts
// for why a ladder cannot express "only this plan".
// Legacy: 'control' → 'menu', 'enterprise' → 'command' (kept for backwards compat)
export type PlanTier = 'free' | 'starter' | 'menu' | 'operate' | 'scale' | 'hotel' | 'command' | 'control' | 'enterprise';

/** Plans that restrict to a single venue */
export const SINGLE_VENUE_PLANS: PlanTier[] = ['free', 'starter', 'menu', 'operate', 'scale', 'control'];
/** Plans that support multiple venues */
export const MULTI_VENUE_PLANS: PlanTier[] = ['hotel', 'command', 'enterprise'];

export function isMultiVenuePlan(plan: PlanTier): boolean {
  return MULTI_VENUE_PLANS.includes(plan);
}

export type VenueRole = 'owner' | 'manager' | 'waiter' | 'kitchen_staff' | 'bar_staff';

export const ROLE_HIERARCHY: Record<VenueRole, number> = {
  owner: 5,
  manager: 4,
  waiter: 3,
  kitchen_staff: 2,
  bar_staff: 2,
};

export interface JwtClaims {
  sub: string;
  email?: string;
  organization_id?: string | null;
  plan?: PlanTier;
  role?: VenueRole | null;
  onboarding_completed?: boolean;
  exp?: number;
  iat?: number;
}

export interface UserProfile {
  id: string;
  organizationId: string | null;
  fullName: string;
  avatarUrl: string | null;
  onboardingCompleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'inactive';

export interface Organization {
  id: string;
  name: string;
  plan: PlanTier;
  // Stripe billing fields (Phase 9)
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  subscriptionStatus: SubscriptionStatus;
  subscriptionActive: boolean;
  currentPeriodEnd?: string | null;
  extraStaffCount: number;
  extraAiCredits: number;
  created_at: string;
  updated_at: string;
}
